import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { buildFieldsHtml, buildFacialHtml, buildLegalHtml, buildSubmitHtml } from '../utils/formPlaceholders';

/**
 * IframeFormRenderer - Renders custom HTML/CSS/JS in a sandboxed iframe
 * 
 * Security: Uses sandbox="allow-scripts allow-forms" - NO allow-same-origin
 * Communication: Uses postMessage for form submission from iframe to parent
 */
const IframeFormRenderer = ({ 
  html, 
  css, 
  js, 
  fields = [], 
  termsUrl, 
  privacyUrl,
  onFormSubmit,
  onFormDataChange,
  projectSlug,
  project
}) => {
  const iframeRef = useRef(null);

  // Debug: Log what we're receiving
  useEffect(() => {
    console.log('🖼️ IframeFormRenderer props:', {
      hasHtml: !!html,
      htmlLength: html?.length || 0,
      hasCss: !!css,
      hasJs: !!js,
      fieldsCount: fields?.length || 0,
      fields: fields
    });
  }, [html, css, js, fields]);

  // Build placeholder HTML components
  const fieldsHtml = buildFieldsHtml(fields);
  const facialHtml = buildFacialHtml();
  const legalHtml = buildLegalHtml(termsUrl, privacyUrl);
  const submitHtml = buildSubmitHtml();

  console.log('📝 Placeholders built:', {
    fieldsHtml: fieldsHtml?.substring(0, 200) + '...',
    fieldsCount: fields?.length
  });

  // Replace placeholders in custom HTML
  const processHtml = (htmlContent) => {
    if (!htmlContent) return '';
    
    let processed = htmlContent
      .replace(/\{FIELDS\}/g, fieldsHtml)
      .replace(/\{FACIAL\}/g, facialHtml)
      .replace(/\{LEGAL\}/g, legalHtml)
      .replace(/\{SUBMIT\}/g, submitHtml);
    
    console.log('✅ HTML processed, placeholders replaced');
    return processed;
  };

  // Build the complete iframe document
  const buildIframeContent = () => {
    const processedHtml = processHtml(html);
    
    return `
<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    /* Reset and base styles */
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { 
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      padding: 20px;
      background: #ffffff;
      color: #333;
    }
    
    /* Inject custom CSS */
    ${css || ''}
    
    /* Default form styles if custom CSS doesn't override */
    .form-field {
      margin-bottom: 16px;
    }
    .form-field label {
      display: block;
      margin-bottom: 4px;
      font-weight: 500;
      color: #374151;
    }
    .form-field input[type="text"],
    .form-field input[type="email"],
    .form-field input[type="tel"],
    .form-field input[type="number"],
    .form-field input[type="date"],
    .form-field select,
    .form-field textarea {
      width: 100%;
      padding: 10px 12px;
      border: 1px solid #d1d5db;
      border-radius: 6px;
      font-size: 14px;
      transition: border-color 0.2s;
    }
    .form-field input:focus,
    .form-field select:focus,
    .form-field textarea:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
    
    /* Legal checkbox styles */
    .legal-checkbox {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      margin: 16px 0;
    }
    .legal-checkbox input {
      margin-top: 3px;
    }
    .legal-checkbox label {
      font-size: 14px;
      color: #6b7280;
      line-height: 1.5;
    }
    .legal-checkbox a {
      color: #3b82f6;
      text-decoration: underline;
    }
    
    /* Submit button styles */
    .submit-btn {
      width: 100%;
      padding: 12px 24px;
      background: #3b82f6;
      color: white;
      border: none;
      border-radius: 8px;
      font-size: 16px;
      font-weight: 600;
      cursor: pointer;
      transition: background 0.2s;
    }
    .submit-btn:hover {
      background: #2563eb;
    }
    .submit-btn:disabled {
      background: #9ca3af;
      cursor: not-allowed;
    }
    
    /* Error message */
    .error-msg {
      color: #dc2626;
      font-size: 12px;
      margin-top: 4px;
    }
    
    /* Facial capture placeholder */
    .facial-placeholder {
      padding: 20px;
      background: #f3f4f6;
      border: 2px dashed #d1d5db;
      border-radius: 8px;
      text-align: center;
      color: #6b7280;
    }
  </style>
</head>
<body>
  ${processedHtml}
  
  <script>
    // Wait for DOM to be ready
    document.addEventListener('DOMContentLoaded', function() {
      // Collect form data from iframe
      function getFormData() {
        const formData = {};
        const inputs = document.querySelectorAll('input, select, textarea');
        inputs.forEach(function(input) {
          if (input.name) {
            if (input.type === 'checkbox') {
              formData[input.name] = input.checked;
            } else if (input.type === 'radio') {
              if (input.checked) {
                formData[input.name] = input.value;
              }
            } else {
              formData[input.name] = input.value;
            }
          }
        });
        return formData;
      }
      
      // Validate form before submission
      function validateForm() {
        const requiredInputs = document.querySelectorAll('[required]');
        let isValid = true;
        
        requiredInputs.forEach(function(input) {
          if (!input.value.trim()) {
            isValid = false;
            input.style.borderColor = '#dc2626';
          } else {
            input.style.borderColor = '#d1d5db';
          }
        });
        
        return isValid;
      }
      
      // Handle form submission
      function handleSubmit(e) {
        e.preventDefault();
        
        if (!validateForm()) {
          // Send validation error to parent
          window.parent.postMessage({ 
            type: 'FORM_VALIDATION_ERROR',
            message: 'Por favor complete todos los campos requeridos'
          }, '*');
          return;
        }
        
        const formData = getFormData();
        
        // Send form data to parent via postMessage
        window.parent.postMessage({
          type: 'FORM_SUBMIT',
          data: formData
        }, '*');
      }
      
      // Attach submit handler to form
      const form = document.querySelector('form');
      if (form) {
        form.addEventListener('submit', handleSubmit);
      }
      
      // Also handle button clicks with class 'submit-btn'
      const submitBtns = document.querySelectorAll('.submit-btn');
      submitBtns.forEach(function(btn) {
        btn.addEventListener('click', function(e) {
          if (e.target.type !== 'submit') {
            // It's a custom submit button, not inside a form
            e.preventDefault();
            if (!validateForm()) {
              window.parent.postMessage({ 
                type: 'FORM_VALIDATION_ERROR',
                message: 'Por favor complete todos los campos requeridos'
              }, '*');
              return;
            }
            const formData = getFormData();
            window.parent.postMessage({
              type: 'FORM_SUBMIT',
              data: formData
            }, '*');
          }
        });
      });
      
      // Notify parent that iframe is loaded
      window.parent.postMessage({ type: 'IFRAME_READY' }, '*');
      
      // Send form data changes to parent (debounced)
      let debounceTimer;
      function notifyFormChange() {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(function() {
          const formData = getFormData();
          window.parent.postMessage({
            type: 'FORM_DATA_CHANGE',
            data: formData
          }, '*');
        }, 500);
      }
      
      // Attach change listeners
      const inputs = document.querySelectorAll('input, select, textarea');
      inputs.forEach(function(input) {
        input.addEventListener('change', notifyFormChange);
        input.addEventListener('input', notifyFormChange);
      });
    });
    
    // Also handle DOMContentLoaded in case script is in head
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        window.parent.postMessage({ type: 'IFRAME_READY' }, '*');
      });
    } else {
      // DOM already loaded
      window.parent.postMessage({ type: 'IFRAME_READY' }, '*');
    }
  </script>
${js ? `<script>${js}</script>` : ''}
  </body>
</html>
    `;
  };

  // Listen for messages from iframe
  useEffect(() => {
    const handleMessage = (event) => {
      // Validate origin if needed (in production, check event.origin)
      
      if (event.data.type === 'FORM_SUBMIT') {
        if (onFormSubmit) {
          onFormSubmit(event.data.data);
        }
      } else if (event.data.type === 'FORM_DATA_CHANGE') {
        if (onFormDataChange) {
          onFormDataChange(event.data.data);
        }
      } else if (event.data.type === 'FORM_VALIDATION_ERROR') {
        console.warn('Form validation error:', event.data.message);
      } else if (event.data.type === 'IFRAME_READY') {
        console.log('Iframe content loaded successfully');
      }
    };

    window.addEventListener('message', handleMessage);
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [onFormSubmit, onFormDataChange]);

  // Update iframe content when props change
  useEffect(() => {
    if (iframeRef.current) {
      iframeRef.current.srcdoc = buildIframeContent();
    }
  }, [html, css, js, fields, termsUrl, privacyUrl]);

  return (
    <div className="iframe-form-wrapper" style={{ width: '100%', minHeight: '600px' }}>
      <iframe
        ref={iframeRef}
        srcDoc={buildIframeContent()}
        sandbox="allow-scripts allow-forms"
        style={{
          width: '100%',
          minHeight: '600px',
          height: 'auto',
          border: 'none',
          borderRadius: '8px'
        }}
        title="Custom Registration Form"
      />
    </div>
  );
};

IframeFormRenderer.propTypes = {
  html: PropTypes.string,
  css: PropTypes.string,
  js: PropTypes.string,
  fields: PropTypes.arrayOf(PropTypes.object),
  termsUrl: PropTypes.string,
  privacyUrl: PropTypes.string,
  onFormSubmit: PropTypes.func,
  onFormDataChange: PropTypes.func,
  projectSlug: PropTypes.string,
  project: PropTypes.object
};

IframeFormRenderer.defaultProps = {
  html: '',
  css: '',
  js: '',
  fields: [],
  termsUrl: '/terminos',
  privacyUrl: '/privacidad',
  onFormSubmit: null,
  onFormDataChange: null,
  projectSlug: '',
  project: null
};

export default IframeFormRenderer;
